import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PRODUCT_TYPE_MOVIE_REWARDS } from "@config/config";
import toSeoUrl from "@functions/toSeoUrl";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { PromoContext } from "@context/PromoContext";
import { SearchContext } from "@context/SearchContext";
import LinkTabs from "@components/LinkTabs";
import Loader from "@components/Ui/Loader";
import DateSelector from "@components/V3/DateSelector";
import ExperienceFinder from "@components/V3/ExperienceSelector/ExperienceFinder";
import MovieInformation from "@components/V3/MovieDetails/MovieInformation";
import MovieSessionList from "@components/V3/MovieDetails/MovieSessionList";
import VenueFinder from "@components/V3/VenueFinder";
import { requestUserLocation } from "@utils/locationUtils";
import "./style.scss";

const parseExperiencesFromUrl = (search) => {
    const raw = new URLSearchParams(search).get("experiences");
    if (!raw) return [];
    return raw.split(",").map((v) => parseInt(v, 10)).filter((v) => !isNaN(v));
};

const MovieSessionSelector = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, userCoords, setUserCoords } = useContext(AuthContext);
    const { productTypeId } = useContext(ApplicationContext);

    const {
        movie,
        isLoadingSessions,
        setSelectedExperiences,
        fetchMovie,
        fetchSessionsByMovie,
        primarySessions,
        secondarySessions,
        availableDates,
        setMovie,
        setVenueName,
    } = useContext(SearchContext);

    const { fetchApplyPromoAvailability } = useContext(PromoContext);

    const [selectedDate, setSelectedDate] = useState(null);
    const [sessionsLoaded, setSessionsLoaded] = useState(false);
    const prevMovieIdRef = useRef(null);
    const isMovieRewardsSite = productTypeId === PRODUCT_TYPE_MOVIE_REWARDS;

    const urlVenueSlug = new URLSearchParams(location.search).get("venue_name") ?? "";

    const autoSelectDate = (data, venueSlug) => {
        const findDate = (checkVenue) =>
            data.availableDates?.find((date) => {
                const primaryForDate = data.primarySessions[date] || {};
                const secondaryForDate = data.secondarySessions[date] || {};

                if (checkVenue) {
                    const hasPrimaryAtVenue = Object.keys(primaryForDate).some(
                        (key) => toSeoUrl(key) === checkVenue && primaryForDate[key]?.length > 0
                    );
                    const hasSecondaryAtVenue = Object.keys(secondaryForDate).some(
                        (key) => toSeoUrl(key) === checkVenue && secondaryForDate[key]?.length > 0
                    );
                    return hasPrimaryAtVenue || hasSecondaryAtVenue;
                }

                return Object.keys(primaryForDate).length > 0 || Object.keys(secondaryForDate).length > 0;
            });

        const firstAvailableDate = findDate(venueSlug) ?? findDate(null);

        if (firstAvailableDate) {
            setSelectedDate(firstAvailableDate);
        }
    };

    useEffect(() => {
        let isMounted = true;
        const isNewMovie = prevMovieIdRef.current !== id;

        if (isNewMovie) {
            setSessionsLoaded(false);
            setSelectedDate(null);
            setMovie({});
        }

        (async () => {
            const slugFromPath = location.pathname.split("/").pop();

            if (isNewMovie) {
                const experiencesFromUrl = parseExperiencesFromUrl(location.search);
                setSelectedExperiences(experiencesFromUrl);
            }

            const movieData = await fetchMovie(id, slugFromPath);

            if (!movieData?.title || !isMounted) return;

            const resolvedSlug = toSeoUrl(movieData.title);
            if (resolvedSlug !== slugFromPath) return;

            const fetchSessions = async (coords = null) => {
                const experiencesFromUrl = parseExperiencesFromUrl(location.search);

                const data = await fetchSessionsByMovie({
                    movieId: movieData.id,
                    venueName: urlVenueSlug,
                    ticketClassId: experiencesFromUrl.length > 0 ? experiencesFromUrl : undefined,
                    lat: coords?.latitude,
                    lng: coords?.longitude,
                    search_distance: 25,
                });

                if (!data || !isMounted) return;

                autoSelectDate(data, urlVenueSlug);
            };

            if (userCoords?.latitude && userCoords?.longitude) {
                await fetchSessions(userCoords);
            } else {
                await new Promise((resolve) => {
                    requestUserLocation(async (coords) => {
                        if (coords && isMounted) setUserCoords(coords);
                        await fetchSessions(coords);
                        resolve();
                    });
                });
            }

            if (isMounted) {
                setSessionsLoaded(true);
                prevMovieIdRef.current = id;
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [id, location.pathname, location.search]);

    // Fetch promo availability
    useEffect(() => {
        if (user?.id) {
            fetchApplyPromoAvailability();
        }
    }, [user?.id]);

    const selectedSessions = {
        primary: selectedDate ? (primarySessions[selectedDate] || {}) : {},
        secondary: selectedDate ? (secondarySessions[selectedDate] || {}) : {},
    };

    const handleDateClick = (date) => {
        setSelectedDate(date);
    };

    const handleVenueClick = async (selectedVenue) => {
        setSelectedDate(null);
        const venueSlug = selectedVenue ? toSeoUrl(selectedVenue) : "";

        const params = new URLSearchParams(location.search);
        setVenueName(selectedVenue || "");

        if (venueSlug) {
            params.set("venue_name", venueSlug);
        } else {
            params.delete("venue_name");
        }

        const newUrl = params.toString()
            ? `${location.pathname}?${params.toString()}`
            : location.pathname;

        navigate(newUrl, { replace: true });

        const data = await fetchSessionsByMovie({
            movieId: movie?.id,
            venueName: venueSlug,
            ticketClassId: parseExperiencesFromUrl(location.search).length > 0
                ? parseExperiencesFromUrl(location.search)
                : undefined,
            lat: userCoords?.latitude,
            lng: userCoords?.longitude,
            search_distance: 25,
        });

        if (data) {
            autoSelectDate(data, venueSlug);
        }
    };

    const handleExperienceClick = async (updatedSelections) => {
        setSelectedExperiences(updatedSelections);

        const params = new URLSearchParams(location.search);
        if (updatedSelections.length > 0) {
            params.set("experiences", updatedSelections.join(","));
        } else {
            params.delete("experiences");
        }
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });

        const data = await fetchSessionsByMovie({
            movieId: movie?.id,
            venueName: urlVenueSlug,
            ticketClassId: updatedSelections.length > 0 ? updatedSelections : undefined,
            lat: userCoords?.latitude,
            lng: userCoords?.longitude,
            search_distance: 25,
        });

        if (data) {
            autoSelectDate(data, urlVenueSlug);
        }
    };

    const showLoader = !sessionsLoaded || isLoadingSessions;

    return (
        <>
            <div className="sessions-available__inner">
                <LinkTabs>
                    {isMovieRewardsSite && (
                        <div label="Dates & Sessions">
                            <div className="sessions-available__wrapper">
                                {showLoader ? (
                                    <Loader isLoading={true} text="Fetching Sessions" />
                                ) : (
                                    <>
                                        <DateSelector
                                            uniqueDates={availableDates}
                                            selectedDate={selectedDate}
                                            handleClickDate={handleDateClick}
                                        />
                                        <VenueFinder
                                            handleVenueClick={handleVenueClick}
                                        />
                                        <ExperienceFinder
                                            handleExperienceClick={handleExperienceClick}
                                        />
                                        <MovieSessionList
                                            venueName={urlVenueSlug}
                                            movieTitle={movie?.title}
                                            selection={{ date: selectedDate, venue: urlVenueSlug }}
                                            selectedSessions={selectedSessions}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <div label="Details">
                        <MovieInformation movie={movie} />
                    </div>
                </LinkTabs>
            </div>
        </>
    );
};

export default MovieSessionSelector;
